import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Sparkles, Globe } from "lucide-react";
import LandingSection from "@/components/landing/LandingSection";
import useTabMusic from "@/hooks/useTabMusic";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 91/92 — public landing page. Content is now fetched from the
// admin-managed CMS (`/api/landing/content`). If the request fails
// (e.g. fresh deployment with no data yet) we still render a minimal
// "About" experience using local fallbacks so visitors never see a
// blank page. Admins can preview their unpublished draft by appending
// `?draft=1` to the URL (requires admin token).

const FOOTER_COPY = {
  it: {
    tagline: "Un GDR narrativo scritto dall'intelligenza artificiale. Mobile + Desktop.",
    game: "Gioco", legal: "Legale",
    login: "Accedi", hall: "Hall of Stories", about: "Chi siamo",
    privacy: "Privacy", terms: "Termini", cookies: "Cookie", contact: "Contatti",
    copy: "© 2026 Arcane Chronicles. Tutti i diritti riservati.",
    stamp: "· Scritto in tempo reale dall'IA ·",
    siteTagline: "AI Narrative RPG",
    navLogin: "Accedi",
  },
  en: {
    tagline: "An AI-narrated RPG. Mobile + Desktop.",
    game: "Game", legal: "Legal",
    login: "Sign in", hall: "Hall of Stories", about: "About",
    privacy: "Privacy", terms: "Terms", cookies: "Cookies", contact: "Contact",
    copy: "© 2026 Arcane Chronicles. All rights reserved.",
    stamp: "· Written in real time by AI ·",
    siteTagline: "AI Narrative RPG",
    navLogin: "Sign in",
  },
};

const detectLang = () => {
  try {
    const stored = localStorage.getItem("ac_lang");
    if (stored === "en" || stored === "it") return stored;
    const nav = (navigator.language || "en").slice(0, 2);
    return nav === "it" ? "it" : "en";
  } catch {
    return "en";
  }
};

export default function LandingPage() {
  const [lang, setLang] = useState(detectLang());
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const isDraftMode = new URLSearchParams(location.search).get("draft") === "1";
  // Iter 104 — landing music (browser may block autoplay; AmbientMusic
  // handles the autoplay-unlock listener gracefully).
  useTabMusic('landing');

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        if (isDraftMode) {
          // Preview the draft (admin-only endpoint)
          const token = localStorage.getItem("token");
          if (token) {
            const r = await axios.get(`${API}/api/admin/landing/draft`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (alive) setSections(r.data?.draft?.sections || []);
            return;
          }
        }
        const r = await axios.get(`${API}/api/landing/content`);
        if (alive) setSections(r.data?.sections || []);
      } catch {
        // silent: keep loading=false → renders empty (header+footer still up)
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchContent();
    return () => { alive = false; };
  }, [isDraftMode]);

  const t = FOOTER_COPY[lang];
  const switchLang = (l) => {
    setLang(l);
    try { localStorage.setItem("ac_lang", l); } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-[#f8fafc] overflow-x-hidden">
      <div
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0c]/85 backdrop-blur-md border-b border-[#cfb53b]/15">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-2">
          <a href="#top" className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-9 h-9 border border-[#cfb53b]/40 flex items-center justify-center bg-gradient-to-br from-[#cfb53b]/20 to-transparent">
              <Sparkles className="text-[#cfb53b]" size={16} />
            </div>
            <div>
              <p className="font-heading text-base md:text-lg tracking-[0.18em] text-[#cfb53b] leading-none">Arcane Chronicles</p>
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-[#cfb53b]/40 mt-0.5">{t.siteTagline}</p>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-[#cfb53b]/70" data-testid="landing-lang-switcher">
              <Globe size={12} />
              <button
                onClick={() => switchLang("it")}
                data-testid="landing-lang-it"
                className={`font-heading text-[10px] uppercase tracking-[0.2em] px-1.5 py-1 transition-colors ${lang === "it" ? "text-[#cfb53b]" : "text-[#cfb53b]/40 hover:text-[#cfb53b]/80"}`}
              >IT</button>
              <span className="text-[#cfb53b]/20">·</span>
              <button
                onClick={() => switchLang("en")}
                data-testid="landing-lang-en"
                className={`font-heading text-[10px] uppercase tracking-[0.2em] px-1.5 py-1 transition-colors ${lang === "en" ? "text-[#cfb53b]" : "text-[#cfb53b]/40 hover:text-[#cfb53b]/80"}`}
              >EN</button>
            </div>
            <Link
              to="/auth"
              data-testid="landing-login-btn"
              className="font-heading text-xs md:text-sm uppercase tracking-[0.25em] px-5 md:px-7 py-2.5 border border-[#cfb53b] text-[#cfb53b] hover:bg-[#cfb53b] hover:text-[#0a0a0c] transition-all duration-300"
            >
              {t.navLogin}
            </Link>
          </div>
        </div>
      </header>

      {/* Draft mode banner */}
      {isDraftMode && (
        <div className="fixed top-[60px] inset-x-0 z-40 bg-amber-500/95 text-[#0a0a0c] text-center text-xs font-heading uppercase tracking-[0.2em] py-2 backdrop-blur-md">
          · ANTEPRIMA BOZZA — non pubblicata · <Link to="/admin" className="underline ml-2">Torna al pannello</Link>
        </div>
      )}

      <div id="top" />

      {/* DYNAMIC SECTIONS */}
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        sections
          .filter(s => s.visible !== false)
          .map((s, i) => (
            <LandingSection key={s.id || i} section={s} lang={lang} dark={i % 2 === 1} />
          ))
      )}

      {/* FOOTER */}
      <footer className="bg-[#050506] border-t border-[#cfb53b]/15 px-4 py-14 text-[#cbd5e1]/60">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="text-[#cfb53b]" size={16} />
              <p className="font-heading text-base tracking-[0.18em] text-[#cfb53b]">Arcane Chronicles</p>
            </div>
            <p className="font-body text-sm leading-relaxed max-w-sm">{t.tagline}</p>
          </div>
          <div>
            <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#cfb53b]/70 mb-3">{t.game}</p>
            <ul className="space-y-2 font-body text-sm">
              <li><Link to="/auth" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-login">{t.login}</Link></li>
              <li><Link to="/hall" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-hall">{t.hall}</Link></li>
              <li><Link to="/about" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-about">{t.about}</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#cfb53b]/70 mb-3">{t.legal}</p>
            <ul className="space-y-2 font-body text-sm">
              <li><Link to="/privacy" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-privacy">{t.privacy}</Link></li>
              <li><Link to="/terms" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-terms">{t.terms}</Link></li>
              <li><Link to="/cookies" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-cookies">{t.cookies}</Link></li>
              <li><Link to="/contact" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-contact">{t.contact}</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto pt-8 border-t border-[#cfb53b]/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-body text-xs text-[#cbd5e1]/40">{t.copy}</p>
          <p className="font-heading text-[10px] uppercase tracking-[0.3em] text-[#cfb53b]/40">{t.stamp}</p>
        </div>
      </footer>
    </div>
  );
}
